/**
 * Befund der Karte „Verweigerte Bestellungen" (P5-Review #24, GF: „warum verweigert / was tun" stand nur in einer
 * Fußnote). Rein beschreibend und nur über ABGESCHLOSSENE Wochen — die laufende Woche ist unvollständig und zöge den
 * Schnitt nach unten. „Schon bestellt" ist ein Hinweis, kein Grund (auch Bonitätsfälle haben Vorbestellungen).
 */
import { formatNumber } from '../../../helpers/numberUtils';

export interface BefundWoche {
  laufend: boolean;
  bestellungen: number;
  verweigert: number;
  verweigert_mit_vorbestellung: number;
  verweigert_ohne_vorbestellung: number;
}

/** Unter diesen Mengen steht kein Befund, sondern der Grund dafür. */
export const BEFUND_MIN_WOCHEN = 4;
export const BEFUND_MIN_VERWEIGERT = 10;

export type Befund = { text: string } | { fehlt: string };

const zahl = (v: number): string => (v >= 10 ? formatNumber(v, 0) : formatNumber(v, 1));

export function verweigertBefund(wochen: BefundWoche[]): Befund {
  const fertig = wochen.filter((w) => !w.laufend);
  const v = fertig.reduce((s, w) => s + w.verweigert, 0);
  const b = fertig.reduce((s, w) => s + w.bestellungen, 0);
  const mit = fertig.reduce((s, w) => s + w.verweigert_mit_vorbestellung, 0);
  const ohne = fertig.reduce((s, w) => s + w.verweigert_ohne_vorbestellung, 0);
  if (fertig.length < BEFUND_MIN_WOCHEN) {
    return { fehlt: `Kein Befund: erst ${fertig.length} abgeschlossene Wochen (mindestens ${BEFUND_MIN_WOCHEN}).` };
  }
  if (v < BEFUND_MIN_VERWEIGERT || b === 0) {
    return { fehlt: `Kein Befund: nur ${v} verweigerte Bestellungen in ${fertig.length} Wochen — zu wenige für einen Schnitt.` };
  }
  const n = fertig.length;
  const anteil = (v * 100) / b;
  const vonZehn = Math.round((mit * 10) / v);
  return {
    text:
      `Ø ${zahl(v / n)} verweigerte Bestellungen je Woche (${formatNumber(anteil, 0)} % der Bestellungen, ${n} abgeschlossene Wochen); ` +
      `bei ${vonZehn} von 10 hatte die Adresse kurz davor schon bestellt. ` +
      `Ohne frühere Bestellung im Schnitt ${zahl(ohne / n)} je Woche. Für ein anderes Angebot (z. B. Vorkasse) kämen nur ` +
      'Bonitätsfälle in Frage; wie viele davon hier stecken, sagen die Daten nicht (eine Näherung, kein Nachweis).',
  };
}

/**
 * Hauptkachel der Karte (Persona-Umbau A6): Verweigerte ohne frühere Bestellung im ganzen Fenster und ihr Schnitt je
 * ABGESCHLOSSENER Woche (die laufende ist unvollständig) — dieselbe Rechnung wie der Befundsatz. `null` ohne fertige Woche.
 */
export function ohneFruehereJeWoche(wochen: BefundWoche[]): { summe: number; jeWoche: number | null; wochen: number } {
  const fertig = wochen.filter((w) => !w.laufend);
  const summe = wochen.reduce((s, w) => s + w.verweigert_ohne_vorbestellung, 0);
  const ohneFertig = fertig.reduce((s, w) => s + w.verweigert_ohne_vorbestellung, 0);
  return { summe, jeWoche: fertig.length > 0 ? ohneFertig / fertig.length : null, wochen: fertig.length };
}
